import {
  postRostersByRosterIdDeleteMutation,
  TeamDB,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuProps,
  Icons,
  toast,
  useConfirmStore,
} from "@win-reality/react-ui";
import { useCallback, useMemo, useState } from "react";

import { CreateTeamRosterDialog } from "@/components/CreateTeamRosterDialog";
import { delay } from "@/utils/delay";

const useDropdownMenuItems = ({
  setIsEditNewRosterDialogOpen,
  setIsDropdownMenuOpen,
  rosterUUID,
  rosterName,
}: {
  rosterUUID: string;
  rosterName: string;
  setIsEditNewRosterDialogOpen: (value: boolean) => void;
  setIsDropdownMenuOpen: (value: boolean) => void;
}) => {
  const { confirm } = useConfirmStore.getState();
  const queryClient = useQueryClient();

  const deleteRoster = useMutation({
    ...postRostersByRosterIdDeleteMutation(),
    onError: () => {
      toast("Error deleting roster", { variant: "error" });
    },
    onSettled: () => {
      useConfirmStore.setState({ isOpen: false, isPending: false });
    },
    onSuccess: async () => {
      toast("Roster removed", {
        variant: "success",
      });

      await delay(1000);

      queryClient.invalidateQueries({
        queryKey: ["getTeamsSearch"],
        exact: false,
      });
    },
  });

  const handleDeleteRoster = useCallback(
    async ({ rosterName }: { rosterName: string }) => {
      const isConfirmed = await confirm({
        title: `Confirm ${rosterName} Deletion?`,
        description: "Are you sure you want to remove this roster?",
        confirmText: "Delete Roster",
        confirmButtonVariant: "destructive",
      });

      if (isConfirmed) {
        useConfirmStore.setState({ isPending: true });

        await deleteRoster.mutateAsync({
          path: { roster_id: rosterUUID },
        });
      }
    },
    [confirm, deleteRoster, rosterUUID],
  );

  const dropdownMenuItems = useMemo(() => {
    const items: DropdownMenuProps["items"] = [
      {
        label: "Edit",
        onClick: (e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsEditNewRosterDialogOpen(true);
          setIsDropdownMenuOpen(false);
        },
      },
      <DropdownMenuItem
        onClick={() => handleDeleteRoster({ rosterName })}
        key="delete-roster"
        className="light:text-error"
      >
        Delete Roster
      </DropdownMenuItem>,
    ];

    return items;
  }, [
    handleDeleteRoster,
    rosterName,
    setIsDropdownMenuOpen,
    setIsEditNewRosterDialogOpen,
  ]);

  return { dropdownMenuItems };
};

function MenuItems({ roster, orgId }: { roster: TeamDB; orgId: string }) {
  const rosterId = roster.external_ids?.[0] ?? null;
  const rosterUUID = roster.id;

  if (!rosterId) {
    throw new Error("Roster is missing external_id");
  }

  if (!rosterUUID) {
    throw new Error("Roster is missing id");
  }

  const [isEditRosterDialogOpen, setIsEditRosterDialogOpen] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const rosterName = roster.known_names?.find((name) => name) || "-";

  const { dropdownMenuItems } = useDropdownMenuItems({
    setIsEditNewRosterDialogOpen: (value) => setIsEditRosterDialogOpen(value),
    setIsDropdownMenuOpen: (value) => setIsOpen(value),
    rosterUUID,
    rosterName,
  });

  return (
    <>
      <DropdownMenu
        variant="light"
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
        }}
        align="start"
        items={dropdownMenuItems}
        onClick={(e) => e.stopPropagation()}
        trigger={
          <Button
            variant="ghost"
            size="icon"
            className={cn(isOpen && "bg-[hsla(218,100%,52%,0.1)]")}
          >
            <Icons.EllipsisVertical className="text-blue" />
          </Button>
        }
      />

      {isEditRosterDialogOpen && (
        <CreateTeamRosterDialog
          isOpen={isEditRosterDialogOpen}
          onOpenChange={setIsEditRosterDialogOpen}
          mode="edit"
          organizationId={orgId}
          initialRoster={{
            id: rosterUUID,
            rosterName,
            // @ts-expect-error missing in API type
            teamName: roster.parent_name || undefined,
            playerExternalIds: roster.unofficial_members
              ?.map((m) => m.externalId)
              .filter(Boolean) as string[] | undefined,
            unofficialMembers: roster.unofficial_members,
          }}
        />
      )}
    </>
  );
}

function ExpandButton({ isExpanded }: { isExpanded: boolean }) {
  return (
    <div className="flex items-center">
      <Button
        variant="ghost"
        size="icon-sm"
        className={cn(
          "transition-all",
          "group-hover:bg-[hsla(218,100%,52%,0.1)] hover:bg-[hsla(218,100%,52%,0.1)]",
          isExpanded && "rotate-180 bg-[hsla(218,100%,52%,0.1)]",
        )}
      >
        <Icons.ChevronUp className="text-blue h-5! w-5!" />
      </Button>
    </div>
  );
}
export const useRostersColumns = ({ orgId }: { orgId: string }) => {
  const rostersColumns = useMemo(
    (): ColumnDef<TeamDB>[] => [
      {
        id: "roster-details",
        cell: ({ row }) => {
          const isExpanded = row.getIsExpanded();

          const rosterName =
            row.original.known_names?.find((name) => name) || "-";

          return (
            <div className="flex w-full items-center justify-between px-2">
              <div className="flex items-center">
                <div className="text-sm font-semibold mr-2">{rosterName}</div>
                <MenuItems roster={row.original} orgId={orgId} />
              </div>
              <div className="flex items-center">
                <div className="text-sm font-regular! mr-8">
                  {row.original.unofficial_members?.length ?? 0} Players
                </div>
                <ExpandButton isExpanded={isExpanded} />
              </div>
            </div>
          );
        },
      },
    ],
    [orgId],
  );

  return { rostersColumns };
};
